import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, KeepAlive as _KeepAlive, createElementVNode as _createElementVNode, vShow as _vShow, normalizeClass as _normalizeClass, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "fw-b" }
const _hoisted_2 = {
  class: "ion-justify-content-end d-flex ion-align-items-end flex-d-c",
  style: {"gap":"0.5rem","padding-inline":"5px"}
}
const _hoisted_3 = {
  key: 0,
  class: "fw-b purple-purple-color fs-larger ion-text-right"
}

import { computed, reactive, ref, shallowRef } from "vue";
import { IonButton, IonCol, IonIcon, IonRow, IonToolbar, loadingController, modalController } from "@ionic/vue";
import { route } from "@/router";
import { t } from "@/plugins/vue-i18n";
import { useThrottleFn } from "@vueuse/core";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import ButtonBase from "@/components/button/ButtonBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import OrderDelivery from "@/views/user/order/OrderDelivery.vue";
import OrderPayment from "@/views/user/order/OrderPayment.vue";
import ShoppingCart from "@/views/user/ShoppingCart.vue";
import OrderSuccess from "@/views/user/order/OrderSuccess.vue";

import { useListStore } from "@/stores/lists";
import { useUserStore } from "@/stores/user"
import { useSupplierStore } from "@/stores/suppliers";

import { useLoader } from "@/composables/useLoader";

import { popAlertConfirm, popAlertOk } from "@/functions/alert";
import { missingLocation, missingTimingDelivery, missingTimingPickup } from "@/functions/checks";

import { PERMISSION_KEY } from "@/types/user";
import { CartData, Order } from "@/types/supplier";
import { DELIVERY_TYPE } from "@/types/enum/order";
import { GetListItemsParams } from "@/types/list";
import { INDUSTRY_TYPE } from "@/types/enum/supplierEnum";

import { chevronBack, refreshOutline } from "ionicons/icons";
import convertFormat, { diffDates } from "@/functions/luxon";
import { DateTime } from "luxon";
import { DATE_FORMAT } from "@/types/enum/luxonEnum";
import { usePersistentStore } from "@/stores/persistent";
import { convertNumberWithComma } from "@/functions/converter";
import { CURRENCY_EUR } from "@/assets/variables";


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderModal',
  setup(__props) {

const listStore = useListStore()
const userStore = useUserStore()
const supplierStore = useSupplierStore()
const persistentStore = usePersistentStore()
const { showLoading, hideLoading } = useLoader()

const cartData = reactive<CartData>({
    supplier: {
        id: '',
        name: '',
        email: '',
        customerNo: '',
        industry: INDUSTRY_TYPE.PRIVATE,
        settings: {},
        hasSpecialOffers: false,
        totalCartPosition: 0,
        totalCartItems: 0,
        noListCartPosition: 0,
        noListCartItems: 0,
        showCartTotal: false
    },
    lists: [],
    cartItems: [],
    summary: []
})
const orderView = shallowRef(ShoppingCart)
const ordering = ref<boolean>(false)

const notCart = computed<boolean>(() => {
    return orderView.value.__name === 'OrderDelivery'
})
const locationId = computed<string>(() => String(route.value.params.locationId ?? ''))
const listId = computed<string>(() => String(route.value.params.listId ?? ''))

const summarySum = computed(() => cartData.summary.reduce((accumulator, currentValue) => {
    return accumulator + ((currentValue.quantity ?? 0) * (currentValue.item.price ?? 0))
}, 0))

const refreshShoppingCart = async () => {
    await showLoading()
    await Promise.all([
        listStore.getShoppingCart(locationId.value),
        userStore.getMe()
    ])
    await hideLoading()
}

const timeIssue = async (kind: DELIVERY_TYPE = DELIVERY_TYPE.DELIVERY): Promise<void> => {
    await popAlertOk({
        header: t('alert.discardFormTitle'),
        message: (kind === DELIVERY_TYPE.DELIVERY) ? t('label.timeIssueDelivery') : t('label.timeIssuePickup'),
    })
}

const locationIssue = async (): Promise<void> => {
    await popAlertOk({
        header: t('alert.discardFormTitle'),
        message: t('messages.order.locationIssue')
    })
}

const finishOrder = async () => {
    ordering.value = true
    showLoading(t('messages.ordering'))

    const orderItems = []
    orderItems.push(...cartData.cartItems)
    cartData.lists.forEach((list) => {
        orderItems.push(...list.cartItems)
    })
    listStore.order.supplierId = cartData.supplier.id
    listStore.order.items = orderItems

    const response = await listStore.createOrder(locationId.value)

    if (!response) {
        ordering.value = false
        orderView.value = ShoppingCart
        hideLoading()
        return
    }

    orderView.value = OrderSuccess

    try {
        await supplierStore.changeSupplierSettings(
            route.value.params.locationId as string,
            cartData.supplier.id,
            cartData.supplier.settings
        )
    } catch (e) {
        console.error("ERROR SAVING SUPPLIER SETTINGS", e)
    }

    await Promise.all([
        listStore.resetOrder(locationId.value),
        listStore.getAllOrders(locationId.value)
    ])

    if (listId.value) await listStore.getListItems(new GetListItemsParams({
        locationId: locationId.value,
        listId: listId.value
    }))

    ordering.value = false

    hideLoading()
}

const hasRequiredData = (cart: CartData): boolean => {
    const supplierId = cart.supplier?.id;
    const hasPickupLocations = (cart.supplier?.pickupLocations?.length ?? 0) > 0
    const hasDeliveryDays = !!supplierStore.deliveryDays[supplierId];
    const hasPickupDays = !!supplierStore.pickupDays[supplierId];

    return hasPickupLocations ? hasDeliveryDays && hasPickupDays : hasDeliveryDays;
    // return !!supplierStore.deliveryDays[cart.supplier?.id];
}

const goNext = async (item: CartData): Promise<void> => {
    if (item) Object.assign(cartData, item)

    const { showLoading, hideLoading } = useLoader()

    switch (orderView.value.__name) {
        case 'ShoppingCart':
            if (!hasRequiredData(item)) {
                await showLoading(t('label.orderDataLoading'), 5000)
                await supplierStore.getSupplierDeliveryDays(locationId.value, item.supplier.id)
                if (item.supplier.pickupLocations?.length) await supplierStore.getSupplierPickupDays(locationId.value, item.supplier.id)
            }
            orderView.value = OrderDelivery
            listStore.order = new Order()
            if (await loadingController.getTop()) await hideLoading()
            break;
        case 'OrderDelivery':
            orderView.value = OrderPayment
            break;
        case 'OrderPayment':
            if (ordering.value) return
            if (missingLocation()) return await locationIssue()
            if (listStore.order.selectedDeliveryType === DELIVERY_TYPE.DELIVERY && missingTimingDelivery(cartData)) return await timeIssue(DELIVERY_TYPE.DELIVERY)
            if (listStore.order.selectedDeliveryType === DELIVERY_TYPE.PICKUP && missingTimingPickup(cartData)) return await timeIssue(DELIVERY_TYPE.PICKUP)

            if (listStore.order.deliveryDate && (diffDates(DateTime.fromISO(listStore.order.deliveryDate), DateTime.now(), ["days"]).days ?? 0) > 7) {
                popAlertConfirm({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    message: t('messages.longOrder', { date: convertFormat(listStore.order.deliveryDate, 'iso', DATE_FORMAT.DE) }),
                    okText: t('button.finishOrder'),
                    cancelText: t('button.cancel'),
                    handler: {
                        ok: finishOrder
                    }
                })
                return
            }

            await finishOrder()

            break;
        case 'OrderSuccess':
            modalController.dismiss();
            break;
        default:
            break;
    }
}

const goNextThrottle = useThrottleFn(goNext, 200)

const goBack = (): void => {
    switch (orderView.value.__name) {
        case 'ShoppingCart':
            modalController.dismiss()
            break;
        case 'OrderDelivery':
            orderView.value = ShoppingCart
            break;
        case 'OrderPayment':
            orderView.value = OrderDelivery
            break;
        default:
            break;
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalControllerBase, {
    "content-class": "light-light-background",
    footer: "",
    "no-footer-padding": orderView.value.__name === 'OrderDelivery'
  }, {
    header: _withCtx(() => [
      _createVNode(CustomHeader, null, {
        start: _withCtx(() => [
          (orderView.value.__name !== 'OrderSuccess')
            ? (_openBlock(), _createBlock(_unref(IonButton), {
                key: 0,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (goBack())),
                class: "purple-light-color"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonIcon), { icon: _unref(chevronBack) }, null, 8, ["icon"]),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('button.back')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        end: _withCtx(() => [
          (orderView.value.__name !== 'ShoppingCart')
            ? (_openBlock(), _createBlock(_unref(IonButton), {
                key: 0,
                class: "purple-light-color",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(modalController).dismiss()))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('button.cancel')), 1)
                ]),
                _: 1
              }))
            : (orderView.value.__name !== 'OrderSuccess')
              ? (_openBlock(), _createBlock(_unref(IonButton), {
                  key: 1,
                  class: "purple-light-color",
                  onClick: refreshShoppingCart
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonIcon), { icon: _unref(refreshOutline) }, null, 8, ["icon"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
        ]),
        default: _withCtx(() => [
          (orderView.value.__name === 'ShoppingCart')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode(_toDisplayString(_ctx.$t('titleRoute.cart')), 1)
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createTextVNode(_toDisplayString(_ctx.$t('titleRoute.orders')), 1)
              ], 64))
        ]),
        _: 1
      })
    ]),
    footer: _withCtx(() => [
      _withDirectives(_createVNode(_unref(IonToolbar), {
        class: _normalizeClass(["grey4-grey4-background purple-purple-color no-padding-toolbar", {'no-padding': orderView.value.__name === 'OrderDelivery'}]),
        style: {"--border-width":"0"}
      }, {
        default: _withCtx(() => [
          (cartData && cartData.supplier.preOrderHint)
            ? (_openBlock(), _createBlock(_unref(IonRow), {
                key: 0,
                class: "light-light-color p-0 max-width",
                style: {"background":"var(--ion-color-danger)"}
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonCol), { class: "px-3" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_1, _toDisplayString(cartData.supplier.preOrderHint), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          cartData
            ? (_openBlock(), _createBlock(_unref(IonRow), {
                key: 1,
                class: "grey4-grey4-background py-2 fs-smaller grey1-grey1-color"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonCol), {
                    size: "auto",
                    class: "d-flex flex-d-c ion-justify-content-end"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(IonRow), null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(IonCol), null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('messages.cart.amountItems', { amount: cartData.supplier?.totalCartItems ?? 0 })), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(IonRow), null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(IonCol), { class: "py-0 center-y" }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('messages.cart.amount', { amount: cartData.supplier?.totalCartPosition ?? 0 })), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(IonCol), null, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_2, [
                        (cartData.supplier.showCartTotal && summarySum.value > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(convertNumberWithComma)(summarySum.value.toFixed(3))) + " " + _toDisplayString(_unref(CURRENCY_EUR)) + " " + _toDisplayString(_ctx.$t('object.total')), 1))
                          : _createCommentVNode("", true),
                        _createVNode(ButtonBase, {
                          disabled: (orderView.value.__name === 'OrderPayment' && !_unref(userStore).permission[_unref(PERMISSION_KEY).ORDER_CREATE]) || _unref(persistentStore).noConnection,
                          class: "max-height m-0 wrap-button",
                          style: {"min-width":"120px"},
                          onClick: _unref(goNextThrottle)
                        }, {
                          default: _withCtx(() => [
                            (orderView.value.__name === 'OrderDelivery')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(_toDisplayString(_ctx.$t('button.forward')), 1)
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode(_toDisplayString(_ctx.$t('button.finishOrder')), 1)
                                ], 64))
                          ]),
                          _: 1
                        }, 8, ["disabled", "onClick"])
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["class"]), [
        [_vShow, notCart.value && orderView.value !== OrderSuccess]
      ])
    ]),
    default: _withCtx(() => [
      (_openBlock(), _createBlock(_KeepAlive, null, [
        (_openBlock(), _createBlock(_resolveDynamicComponent(orderView.value), {
          cartData: cartData,
          onNext: _unref(goNextThrottle),
          onBack: goBack
        }, null, 40, ["cartData", "onNext"]))
      ], 1024))
    ]),
    _: 1
  }, 8, ["no-footer-padding"]))
}
}

})